

import type { CampaignEditingMessage } from '@/src/contexts/campaign-editing-context';

const ENV_PREFIX = process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? 'p-' : 's-';

/**
 * Service for publishing campaign editing messages to PubNub from non-React contexts
 */
class CampaignEditingService {
  private static instance: CampaignEditingService | null = null;
  private pubnub: any | null = null;
  private readonly channelName = 'clickster-campaign-editing';

  private constructor() {}

  /**
   * Get singleton instance of CampaignEditingService
   */
  static getInstance(): CampaignEditingService {
    if (!CampaignEditingService.instance) {
      CampaignEditingService.instance = new CampaignEditingService();
    }
    return CampaignEditingService.instance;
  }

  /**
   * Initialize PubNub connection if not already initialized
   */
  private async initializePubNub(): Promise<void> {
    if (this.pubnub) {
      return;
    }

    try {
      // Import PubNub manager dynamically to prevent SSR issues
      const { default: PubNubManager } = await import('../../utils/pubnub');

      const { userId } = this.getUserInfo();
      const tabId = Math.random().toString(36).substring(2, 8);
      const uniqueUserId = `${userId}-tab-${tabId}`;

      const pubNubManager = new PubNubManager(uniqueUserId);
      this.pubnub = pubNubManager.getPubNubInstance();
    } catch (error) {
      console.error('[CampaignEditingService] Failed to initialize PubNub:', error);
      throw error;
    }
  }

  /**
   * Get user information from localStorage
   */
  private getUserInfo(): { userId: string; fullName: string } {
    const userId = localStorage.getItem('clk-u') || 'unknown-user';
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const fullName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User';

    return { userId, fullName };
  }

  /**
   * Get the appropriate screen URL based on current location
   */
  private getScreenUrl(): string | null {
    if (typeof window !== 'undefined') {
      return window.location.pathname;
    }
    return null;
  }

  /**
   * Publish a campaign editing message
   * @param message - The campaign editing message to publish
   */
  async publishEditingMessage(message: CampaignEditingMessage): Promise<void> {
    try {
      await this.initializePubNub();

      if (!this.pubnub) {
        throw new Error('PubNub not initialized');
      }

      const fullChannelName = `${ENV_PREFIX}${this.channelName}`;

      await this.pubnub.publish({
        channel: fullChannelName,
        message,
      });

      console.log('[CampaignEditingService] Published campaign editing message:', message);
    } catch (error) {
      console.error('[CampaignEditingService] Failed to publish campaign editing message:', error);
      throw error;
    }
  }

  /**
   * Publish a campaign creation message
   * @param campaignId - The ID of the created campaign
   */
  async publishCampaignCreated(campaignId: string): Promise<void> {
    const { userId, fullName } = this.getUserInfo();
    const screen = this.getScreenUrl();

    const message: CampaignEditingMessage = {
      campaignId,
      userId,
      fullName,
      updatedAt: new Date().toISOString(),
      screen,
    };

    await this.publishEditingMessage(message);
  }

  /**
   * Publish a campaign updated message
   * @param campaignId - The ID of the updated campaign
   */
  async publishCampaignUpdated(campaignId: string): Promise<void> {
    const { userId, fullName } = this.getUserInfo();
    const screen = this.getScreenUrl();

    const message: CampaignEditingMessage = {
      campaignId,
      userId,
      fullName,
      updatedAt: new Date().toISOString(),
      screen,
    };

    await this.publishEditingMessage(message);
  }
}

export default CampaignEditingService;
